/**
 * Data layer for `publisher-performance-report`. Typed fetch wrappers over
 * /api/v1/publisher-performance-report rollup + drill endpoints.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export type GroupBy = "day" | "week" | "month";

export interface SparkPoint {
	bucket: string;
	cvr: number;
}

export interface PublisherRow {
	affiliate_id: string;
	affiliate_name: string;
	clicks: number;
	conversions: number;
	cr: number;
	payout: number;
	revenue: number;
	profit: number;
	epc: number;
	spark: SparkPoint[];
}

export interface CompareRow {
	affiliate_id: string;
	clicks_delta_pct: number;
	conversions_delta_pct: number;
	cr_delta_pct: number;
	payout_delta_pct: number;
	revenue_delta_pct: number;
	profit_delta_pct: number;
	epc_delta_pct: number;
}

export interface KpiTotals {
	active_affiliates: number;
	total_clicks: number;
	total_conversions: number;
	total_payout: number;
	avg_cr: number;
	avg_epc: number;
}

export interface KpiDeltas {
	active_affiliates_pct: number;
	total_clicks_pct: number;
	total_conversions_pct: number;
	total_payout_pct: number;
	avg_cr_pct: number;
	avg_epc_pct: number;
}

export interface RollupResponse {
	from: string;
	to: string;
	group_by: GroupBy;
	totals: KpiTotals;
	totals_prev?: KpiTotals;
	deltas?: KpiDeltas;
	rows: PublisherRow[];
	compare?: CompareRow[];
}

export interface DrillCampaignRow {
	campaign_id: string;
	campaign_name: string;
	clicks: number;
	conversions: number;
	cr: number;
	payout: number;
	revenue: number;
	profit: number;
	epc: number;
}

export interface DrillResponse {
	affiliate_id: string;
	from: string;
	to: string;
	campaigns: DrillCampaignRow[];
}

export interface RollupArgs {
	from: string;
	to: string;
	groupBy: GroupBy;
	compare?: boolean;
}

export interface DrillArgs {
	affiliateId: string;
	from: string;
	to: string;
}

const BASE = "/api/v1/publisher-performance-report";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function publisherRollup(args: RollupArgs): Promise<RollupResponse> {
	const params = new URLSearchParams({
		from: args.from,
		to: args.to,
		group_by: args.groupBy,
	});
	if (args.compare) params.set("compare", "1");
	const res = await fetch(`${BASE}/rollup?${params.toString()}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<RollupResponse>(res);
}

export async function publisherDrill(args: DrillArgs): Promise<DrillResponse> {
	const params = new URLSearchParams({
		affiliate_id: args.affiliateId,
		from: args.from,
		to: args.to,
	});
	const res = await fetch(`${BASE}/drill?${params.toString()}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<DrillResponse>(res);
}

export const publisherPerformanceReportQueryKeys = {
	all: ["publisher-performance-report"] as const,
	rollup: (args: RollupArgs) =>
		[
			...["publisher-performance-report"],
			"rollup",
			args.from,
			args.to,
			args.groupBy,
			args.compare ? "cmp" : "nocmp",
		] as const,
	drill: (args: DrillArgs) =>
		[
			...["publisher-performance-report"],
			"drill",
			args.affiliateId,
			args.from,
			args.to,
		] as const,
};
